import { Container, Sprite, Assets, Text, TextStyle } from 'pixi.js';
import CardAnimations from './CardAnimations.js';
import { GAME_TIMINGS } from '../config/gameTimings.js';
import { ANIMATION_DURATIONS } from '../config/animationConfig.js';
import { isLocalPlayer } from './seatUtils.js';

import { debugLog, debugError, debugWarn, debugTable, debugGroup, debugGroupEnd } from '../utils/debugUtils.js';
/**
 * DealManagerRenderer class handles the visual animations for dealing cards.
 * 
 * Features:
 * - Animates card extraction from the deck (top card first)
 * - Deals cards to the center of the table
 * - Deals cards to each player's position
 * - Shows cards face-up or face-down based on position
 * - Handles the complete dealing sequence with proper timing
 */
class DealManagerRenderer {
    constructor(app, deckRenderer, dealManager) {
        this.app = app;
        this.deckRenderer = deckRenderer;
        this.dealManager = dealManager;

        // Card dimensions
        this.cardWidth = 100;
        this.cardHeight = 150;

        // Containers for different areas
        this.centerCardsContainer = null;
        this.playerCardsContainers = {};

        // Player name texts (displayed behind cards)
        this.playerNameTexts = {};

        // Slot fill order: center slots first, then outer slots
        // This ensures the first 4 cards are centered (slots 2, 3, 1, 4)
        this.slotFillOrder = [2, 3, 1, 4, 0, 5, 8, 9, 7, 10, 6, 11];

        this.initialize();
    }

    /**
     * Get deal duration dynamically from config (supports live speed changes)
     */
    get dealDuration() {
        return ANIMATION_DURATIONS.DEAL_DURATION;
    }

    /**
     * Get deal delay dynamically from config (supports live speed changes)
     */
    get dealDelay() {
        return ANIMATION_DURATIONS.DEAL_DELAY;
    }

    /**
     * Update the deck renderer reference
     * @param {DeckRenderer} deckRenderer 
     */
    setDeckRenderer(deckRenderer) {
        this.deckRenderer = deckRenderer;
    }

    /**
     * Initialize containers for cards
     */
    initialize() {
        // Container for center cards
        this.centerCardsContainer = new Container();
        this.centerCardsContainer.label = 'CenterCards';
        this.centerCardsContainer.zIndex = 100;
        this.app.stage.addChild(this.centerCardsContainer);

        // Containers for each player's cards
        for (let i = 0; i < 4; i++) {
            const container = new Container();
            container.label = `Player${i + 1}Cards`;
            container.zIndex = 100;
            container.sortableChildren = true; // Enable z-index sorting within container
            this.app.stage.addChild(container);
            this.playerCardsContainers[i] = container;
        }

        this.app.stage.sortableChildren = true;
    }

    /**
     * Set player names and avatars to display on the table
     * Names are positioned in each player's zone, behind their cards
     * @param {Array} players - Array of player objects with name and avatar properties
     */
    setPlayerNames(players) {
        if (!players || players.length < 4) {
            debugWarn('[DealManagerRenderer] setPlayerNames called with invalid players array');
            return;
        }

        // Remove existing name texts if any
        Object.values(this.playerNameTexts).forEach(item => {
            if (item) {
                if (item.name && !item.name.destroyed) item.name.destroy();
                if (item.avatar && !item.avatar.destroyed) item.avatar.destroy();
            }
        });
        this.playerNameTexts = {};

        const screenWidth = this.app.screen.width;
        const screenHeight = this.app.screen.height;
        const centerX = screenWidth / 2;
        const centerY = screenHeight / 2;
        const margin = this.cardHeight * 0.6;

        // Text style for player names
        const nameStyle = new TextStyle({
            fontFamily: 'Arial, sans-serif',
            fontSize: 16,
            fontWeight: 'bold',
            fill: 0xFFFFFF,
            stroke: { color: 0x000000, width: 3 },
            dropShadow: {
                color: 0x000000,
                blur: 4,
                angle: Math.PI / 4,
                distance: 2,
            },
        });

        // Text style for avatars (emojis)
        const avatarStyle = new TextStyle({
            fontFamily: 'Arial, sans-serif',
            fontSize: 28,
        });

        const avatarOffset = 35; // Distance between avatar and name

        for (let i = 0; i < 4; i++) {
            const player = players[i];

            // Create name text
            const nameText = new Text({ text: player.name, style: nameStyle });
            nameText.anchor.set(0.5);
            nameText.zIndex = 1;

            // Create avatar text (emoji)
            const avatarText = new Text({ text: player.avatar || '😀', style: avatarStyle });
            avatarText.anchor.set(0.5);
            avatarText.zIndex = 1;

            switch (i) {
                case 0: // Bottom player (human) - avatar above name
                    nameText.x = centerX;
                    nameText.y = screenHeight - margin + 12;
                    avatarText.x = centerX;
                    avatarText.y = screenHeight - margin - avatarOffset + 12;
                    nameText.rotation = 0;
                    avatarText.rotation = 0;
                    break;
                case 1: // Right player - avatar closer to center than name
                    nameText.x = screenWidth - margin + 12;
                    nameText.y = centerY;
                    avatarText.x = screenWidth - margin - avatarOffset + 12;
                    avatarText.y = centerY;
                    nameText.rotation = -Math.PI / 2;
                    avatarText.rotation = -Math.PI / 2;
                    break;
                case 2: // Top player - avatar below name (inverted)
                    nameText.x = centerX;
                    nameText.y = margin - 12;
                    avatarText.x = centerX;
                    avatarText.y = margin + avatarOffset - 12;
                    nameText.rotation = 0;
                    avatarText.rotation = 0;
                    break;
                case 3: // Left player - avatar closer to center than name
                    nameText.x = margin - 12;
                    nameText.y = centerY;
                    avatarText.x = margin + avatarOffset - 12;
                    avatarText.y = centerY;
                    nameText.rotation = Math.PI / 2;
                    avatarText.rotation = Math.PI / 2;
                    break;
            }

            this.app.stage.addChild(avatarText);
            this.app.stage.addChild(nameText);
            this.playerNameTexts[i] = { name: nameText, avatar: avatarText };
        }

        debugLog('[DealManagerRenderer] Player names set:', players.map(p => `${p.avatar} ${p.name}`));
    }

    /**
     * Get the position for center cards using a 2-row slot system
     * 12 slots total: 6 on top row, 6 on bottom row
     * @param {number} slotIndex - Slot index (0-11)
     * @returns {Object} Position {x, y, slotIndex}
     */
    getCenterCardPosition(slotIndex) {
        if (!this.app || !this.app.renderer || !this.app.screen) return { x: 0, y: 0, slotIndex: 0 };
        const screenWidth = this.app.screen.width;
        const screenHeight = this.app.screen.height;
        const centerX = screenWidth / 2;
        const centerY = screenHeight / 2;

        // 2 rows of 6 slots each
        const slotsPerRow = 6;
        const spacing = this.cardWidth + 10;
        const rowSpacing = this.cardHeight + 15;

        const row = Math.floor(slotIndex / slotsPerRow);
        const posInRow = slotIndex % slotsPerRow;

        const totalRowWidth = slotsPerRow * this.cardWidth + (slotsPerRow - 1) * 10;
        const startX = centerX - totalRowWidth / 2 + this.cardWidth / 2;
        const x = startX + posInRow * spacing;

        const y = row === 0
            ? centerY - rowSpacing / 2
            : centerY + rowSpacing / 2;

        return {
            x: x,
            y: y,
            slotIndex: slotIndex
        };
    }

    /**
     * Get position for Baciga discard pile (center of table)
     * Used when players pass and throw their cards face-down
     * Cards stack with slight random offset for natural look
     * @param {number} cardIndex - Index of the discard (for offset)
     * @returns {Object} Position {x, y, rotation}
     */
    getBacigaDiscardPosition(cardIndex = 0) {
        if (!this.app || !this.app.renderer || !this.app.screen) return { x: 0, y: 0, rotation: 0 };
        const screenWidth = this.app.screen.width;
        const screenHeight = this.app.screen.height;
        const centerX = screenWidth / 2;
        const centerY = screenHeight / 2;

        // Random offset for stacking effect
        const randomOffsetX = (Math.random() - 0.5) * 20;
        const randomOffsetY = (Math.random() - 0.5) * 20;
        const randomRotation = (Math.random() - 0.5) * 0.3; // ~17 degrees max

        return {
            x: centerX + randomOffsetX,
            y: centerY + randomOffsetY,
            rotation: randomRotation
        };
    }

    /**
     * Get position for Baciga "Las 31" phase - cards laid out face-up
     * Each player shows their cards in their area of the table
     * Cards are spread horizontally (or vertically for side players)
     * Supports multiple rows/columns when cards don't fit on screen
     * 
     * @param {number} playerIndex - Index of the player (0-3)
     * @param {number} cardIndex - Index of the card in hand
     * @param {number} totalCards - Total number of cards (for centering)
     * @param {number} stackOffset - Offset for stacked cards of same value (0 = base, 1+ = stacked)
     * @returns {Object} Position {x, y, rotation}
     */
    getBaciga31Position(playerIndex, cardIndex, totalCards = 3, stackOffset = 0) {
        if (!this.app || !this.app.renderer || !this.app.screen) return { x: 0, y: 0, rotation: 0 };
        const screenWidth = this.app.screen.width;
        const screenHeight = this.app.screen.height;
        const centerX = screenWidth / 2;
        const centerY = screenHeight / 2;

        // REDUCED SPACING: Use 0.85x card width for tighter layout without overlap
        const spacing = this.cardWidth * 0.85;
        const margin = this.cardHeight * 0.8; // Distance from edge
        const rowSpacing = this.cardHeight * 0.7; // Space between rows

        // Offset for stacked cards of same value (25% of card height)
        const stackOffsetAmount = this.cardHeight * 0.25;

        let position = { x: 0, y: 0, rotation: 0 };

        switch (playerIndex) {
            case 0: // Player 1 - Bottom (human player)
            case 2: // Player 3 - Top
                // Horizontal spread - calculate if we need multiple rows
                const maxCardsPerRow = Math.floor((screenWidth - margin * 2) / spacing);
                const numRows = Math.ceil(totalCards / maxCardsPerRow);
                const rowIndex = Math.floor(cardIndex / maxCardsPerRow);
                const cardIndexInRow = cardIndex % maxCardsPerRow;
                const cardsInThisRow = Math.min(maxCardsPerRow, totalCards - rowIndex * maxCardsPerRow);
                
                const rowWidth = (cardsInThisRow - 1) * spacing;
                const rowStartOffset = -rowWidth / 2;

                if (playerIndex === 0) {
                    // Bottom player - rows go upward toward center
                    position = {
                        x: centerX + rowStartOffset + cardIndexInRow * spacing,
                        y: screenHeight - margin - rowIndex * rowSpacing - (stackOffset * stackOffsetAmount),
                        rotation: 0
                    };
                } else {
                    // Top player - rows go downward toward center
                    position = {
                        x: centerX - rowStartOffset - cardIndexInRow * spacing,
                        y: margin + rowIndex * rowSpacing + (stackOffset * stackOffsetAmount),
                        rotation: Math.PI
                    };
                }
                break;

            case 1: // Player 2 - Right
            case 3: // Player 4 - Left
                // Vertical spread - calculate if we need multiple columns
                const maxCardsPerCol = Math.floor((screenHeight - margin * 2) / spacing);
                const numCols = Math.ceil(totalCards / maxCardsPerCol);
                const colIndex = Math.floor(cardIndex / maxCardsPerCol);
                const cardIndexInCol = cardIndex % maxCardsPerCol;
                const cardsInThisCol = Math.min(maxCardsPerCol, totalCards - colIndex * maxCardsPerCol);
                
                const colHeight = (cardsInThisCol - 1) * spacing;
                const colStartOffset = -colHeight / 2;

                if (playerIndex === 1) {
                    // Right player - columns go left toward center
                    position = {
                        x: screenWidth - margin - colIndex * rowSpacing - (stackOffset * stackOffsetAmount),
                        y: centerY + colStartOffset + cardIndexInCol * spacing,
                        rotation: -Math.PI / 2
                    };
                } else {
                    // Left player - columns go right toward center
                    position = {
                        x: margin + colIndex * rowSpacing + (stackOffset * stackOffsetAmount),
                        y: centerY - colStartOffset - cardIndexInCol * spacing,
                        rotation: Math.PI / 2
                    };
                }
                break;
        }

        return position;
    }

    /**
     * Find the next available slot, filling from center outward
     * Order: [2,3,1,4,0,5] for top row, then [8,9,7,10,6,11] for bottom row
     * @returns {number} Next available slot index (0-11)
     */
    getNextAvailableSlot() {
        const occupiedSlots = this.centerCardsContainer.children.map(sprite => sprite.slotIndex || 0);
        debugLog('[getNextAvailableSlot] Occupied slots:', occupiedSlots);

        for (const slot of this.slotFillOrder) {
            if (!occupiedSlots.includes(slot)) {
                debugLog('[getNextAvailableSlot] Next available slot:', slot);
                return slot;
            }
        }

        debugLog('[getNextAvailableSlot] All slots filled, returning slot 11');
        return 11;
    }

    /**
     * Get the position for a player's cards
     * Player positions:
     * - Player 0 (bottom): Cards below center
     * - Player 1 (right): Cards on right side
     * - Player 2 (top): Cards above center
     * - Player 3 (left): Cards on left side
     * 
     * @param {number} playerIndex - Index of the player (0-3)
     * @param {number} cardIndex - Index of the card (0-2)
     * @returns {Object} Position {x, y, rotation}
     */
    getPlayerCardPosition(playerIndex, cardIndex) {
        if (!this.app || !this.app.renderer || !this.app.screen) return { x: 0, y: 0, rotation: 0 };
        const screenWidth = this.app.screen.width;
        const screenHeight = this.app.screen.height;
        const centerX = screenWidth / 2;
        const centerY = screenHeight / 2;

        // Reduce spacing for tighter card arrangement (overlapping effect)
        const spacing = this.cardWidth * 0.4; // Cards overlap significantly
        const margin = this.cardHeight * 0.6; // Closer to edges (60% of card height)

        let position = { x: 0, y: 0, rotation: 0 };

        switch (playerIndex) {
            case 0: // Player 1 - Bottom (human player) - Fan effect
                // Horizontal arrangement at bottom with fan effect
                const fanRadius = this.cardWidth * 4; // Radius of the arc
                const fanAngle = 0.25; // Base angle step (~14 degrees)
                // Center the fan around index 1 (approx center of 3 cards). 
                // For N cards it will fan out to the right. 
                // To keep it centered we would need total count, but fanning right is acceptable for "adding cards".
                const cardAngle = (cardIndex - 1) * fanAngle;

                // Calculate position on arc
                const arcX = centerX + Math.sin(cardAngle) * fanRadius;
                const arcY = screenHeight - margin + (1 - Math.cos(cardAngle)) * this.cardHeight * 0.3;

                position = {
                    x: arcX,
                    y: arcY,
                    rotation: cardAngle
                };
                break;

            case 1: // Player 2 - Right
                // Vertical arrangement on right (rotated -90°), cards overlapping
                const totalHeightRight = 2 * spacing; // Only spacing matters now
                const startYRight = centerY - totalHeightRight / 2;
                position = {
                    x: screenWidth - margin,
                    y: startYRight + cardIndex * spacing,
                    rotation: -Math.PI / 2
                };
                break;

            case 2: // Player 3 - Top
                // Horizontal arrangement at top (rotated 180°), cards overlapping
                const totalWidthTop = 2 * spacing;
                const startXTop = centerX + totalWidthTop / 2;
                position = {
                    x: startXTop - cardIndex * spacing,
                    y: margin,
                    rotation: Math.PI
                };
                break;

            case 3: // Player 4 - Left
                // Vertical arrangement on left (rotated 90°), cards overlapping
                const totalHeightLeft = 2 * spacing;
                const startYLeft = centerY + totalHeightLeft / 2;
                position = {
                    x: margin,
                    y: startYLeft - cardIndex * spacing,
                    rotation: Math.PI / 2
                };
                break;
        }

        return position;
    }

    /**
     * Get card image path
     */
    getCardImagePath(suit, value) {
        const suitMap = {
            'oros': 1,
            'copas': 2,
            'espadas': 3,
            'bastos': 4
        };

        const suitCode = suitMap[suit];
        const valueCode = value.toString().padStart(2, '0');

        return `assets/images/deck/${suitCode}${valueCode}.png`;
    }

    /**
     * Get card back image path
     */
    getCardBackPath() {
        const cardBack = localStorage.getItem('cardBack') || '001';
        return `assets/images/deck/${cardBack}.png`;
    }

    /**
     * Create a card sprite
     * @param {Object} card - Card data
     * @param {boolean} faceUp - Whether to show face up
     * @returns {Promise<Sprite>} Card sprite
     */
    async createCardSprite(card, faceUp = false) {
        const texturePath = faceUp
            ? this.getCardImagePath(card.suit, card.value)
            : this.getCardBackPath();

        const texture = await Assets.load(texturePath);
        const sprite = new Sprite(texture);

        sprite.width = this.cardWidth;
        sprite.height = this.cardHeight;
        sprite.anchor.set(0.5);

        // Store card data
        sprite.cardData = card;
        sprite.isFaceUp = faceUp;

        return sprite;
    }

    /**
     * Create a card sprite with interaction support
     * @param {Object} card - Card data
     * @param {boolean} faceUp - Whether to show face up
     * @returns {Promise<Sprite>} Card sprite
     */
    async createCardSprite(card, faceUp = false) {
        const texturePath = faceUp
            ? this.getCardImagePath(card.suit, card.value)
            : this.getCardBackPath();

        const texture = await Assets.load(texturePath);
        const sprite = new Sprite(texture);

        sprite.width = this.cardWidth;
        sprite.height = this.cardHeight;
        sprite.anchor.set(0.5);

        // Store card data
        sprite.cardData = card;
        sprite.isFaceUp = faceUp;

        // Enable interaction
        sprite.eventMode = 'static';
        sprite.cursor = 'pointer';

        sprite.on('pointerdown', (event) => {
            if (this.onCardClick) {
                // Find which player interaction this belongs to?
                // Actually we can just find the sprite in our containers to know who it belongs to
                // But it's easier if we tag it or if we search.

                // Let's search
                let playerIndex = -1;
                let cardIndex = -1;

                for (let i = 0; i < 4; i++) {
                    const container = this.playerCardsContainers[i];
                    const idx = container.children.indexOf(sprite);
                    if (idx !== -1) {
                        playerIndex = i;
                        cardIndex = idx;
                        break;
                    }
                }

                if (playerIndex !== -1) {
                    this.onCardClick(playerIndex, cardIndex, sprite.cardData);
                }
            }
        });

        return sprite;
    }

    /**
     * Animate dealing the first hand (center cards and player cards)
     * @param {number} dealerIndex - The index of the dealer (0-3)
     * @returns {Promise}
     */
    async animateDealFirstHand(dealerIndex) {
        const dealingOrder = this.dealManager.getDealingOrder(dealerIndex);

        // Clear any existing cards
        this.clearAllCards();

        // Deal 3 rounds: center card -> 4 player cards
        for (let round = 0; round < 3; round++) {
            // Deal one card to center
            await this.animateDealToCenter(round);
            await this.delay(this.dealDelay);

            // Deal one card to each player
            for (const playerIndex of dealingOrder) {
                await this.animateDealToPlayer(playerIndex, round);
                await this.delay(this.dealDelay);
            }
        }

        // Deal the fourth center card
        await this.animateDealToCenter(3);
    }

    /**
     * Animate dealing a card to the center
     * @param {number} cardIndex - Index of the center card (0-3)
     */
    async animateDealToCenter(cardIndex) {
        if (!this.app.renderer) return;
        // Get the card from deck (visually remove top card)
        const topCardSprite = this.deckRenderer.cardSprites[this.deckRenderer.cardSprites.length - 1];
        if (!topCardSprite) return;

        // Get the card data from DealManager
        const centerCards = this.dealManager.getCenterCards();
        const card = centerCards[cardIndex];
        if (!card) return;

        // Create card sprite (face up for center)
        const cardSprite = await this.createCardSprite(card, true);

        // Start at deck position (in world coordinates)
        const deckWorldPos = this.deckRenderer.container.toGlobal({ x: 0, y: 0 });
        cardSprite.x = deckWorldPos.x;
        cardSprite.y = deckWorldPos.y;
        cardSprite.rotation = this.deckRenderer.rotation;

        // Add to center container
        this.centerCardsContainer.addChild(cardSprite);

        // Get target position using slot system
        // Use slotFillOrder to ensure initial deal is centered
        const slotIndex = this.slotFillOrder[cardIndex];
        const targetPos = this.getCenterCardPosition(slotIndex);
        cardSprite.slotIndex = targetPos.slotIndex; // Store slot index

        // Animate: slide to position and rotate to 0
        await Promise.all([
            CardAnimations.slideCard(cardSprite, targetPos, this.dealDuration),
            this.animateRotation(cardSprite, 0, this.dealDuration)
        ]);

        // Remove visual card from deck
        await this.deckRenderer.removeCard(topCardSprite.deckIndex, false);
    }

    /**
     * Animate dealing a card to a player
     * @param {number} playerIndex - Index of the player (0-3)
     * @param {number} cardIndex - Index of the card (0-2)
     */
    async animateDealToPlayer(playerIndex, cardIndex) {
        if (!this.app.renderer) {
            console.error('🎴 [DealManagerRenderer] No renderer available!');
            return;
        }

        // Check if deck is rendered
        if (!this.deckRenderer.container) {
            console.error('🎴 [DealManagerRenderer] Deck container not rendered! Call deckRenderer.render() first.');
            return;
        }

        if (!this.deckRenderer.cardSprites || this.deckRenderer.cardSprites.length === 0) {
            console.error('🎴 [DealManagerRenderer] No card sprites in deck! Deck may not be rendered.');
            return;
        }

        // Get the card from deck
        const topCardSprite = this.deckRenderer.cardSprites[this.deckRenderer.cardSprites.length - 1];
        if (!topCardSprite) {
            console.error('🎴 [DealManagerRenderer] No top card sprite found!');
            return;
        }

        // Get the card data from DealManager
        const playerCards = this.dealManager.getPlayerCards(playerIndex);
        const card = playerCards[cardIndex];
        if (!card) {
            console.error(`🎴 [DealManagerRenderer] No card at index ${cardIndex} for player ${playerIndex}`);
            return;
        }

        debugLog(`🎴 [DealManagerRenderer] Dealing card ${card.value} ${card.suit} to player ${playerIndex}`);

        // Determine if card should be face up
        // - Player 1 (index 0) always sees their cards face up
        // - If torito is enabled and this is the torito card (first card), show face up for all players
        // - Otherwise, cards are face down for AI players
        let faceUp = isLocalPlayer(playerIndex); // Human player always sees face up

        if (this.currentToritoInfo && this.currentToritoInfo.enabled && cardIndex === this.currentToritoInfo.cardIndex) {
            // This is the torito card - show face up for ALL players
            faceUp = true;
            debugLog(`🎴 [DealManagerRenderer] TORITO! Showing card ${cardIndex} face-up for player ${playerIndex}`);
        }

        // Create card sprite
        const cardSprite = await this.createCardSprite(card, faceUp);

        // Start at deck position
        const deckWorldPos = this.deckRenderer.container.toGlobal({ x: 0, y: 0 });
        cardSprite.x = deckWorldPos.x;
        cardSprite.y = deckWorldPos.y;
        cardSprite.rotation = this.deckRenderer.rotation;

        // Add to player container
        this.playerCardsContainers[playerIndex].addChild(cardSprite);

        // Get target position
        const targetPos = this.getPlayerCardPosition(playerIndex, cardIndex);

        // Animate: slide to position and rotate
        await Promise.all([
            CardAnimations.slideCard(cardSprite, { x: targetPos.x, y: targetPos.y }, this.dealDuration),
            this.animateRotation(cardSprite, targetPos.rotation, this.dealDuration)
        ]);

        // Remove visual card from deck
        await this.deckRenderer.removeCard(topCardSprite.deckIndex, false);
    }

    /**
     * Animate dealing subsequent hands (only to players, no center)
     * @param {number} dealerIndex - The index of the dealer (0-3)
     */
    async animateDealSubsequentHand(dealerIndex) {
        const dealingOrder = this.dealManager.getDealingOrder(dealerIndex);

        // Clear player cards only (keep center cards)
        this.clearPlayerCards();

        // Deal 3 cards to each player
        for (let round = 0; round < 3; round++) {
            for (const playerIndex of dealingOrder) {
                await this.animateDealToPlayer(playerIndex, round);
                await this.delay(this.dealDelay);
            }
        }
    }

    /**
     * Animate dealing cards for Baciga - 3 cards to each player, no center cards
     * According to the rules: Cards are dealt one at a time in counter-clockwise order
     * starting from the player to the dealer's right
     *
     * @param {number} dealerIndex - The index of the dealer (0-3)
     * @param {Object} toritoInfo - Information about torito { enabled: boolean, cardIndex: number }
     */
    async animateDealBacigaHand(dealerIndex, toritoInfo = { enabled: false, cardIndex: null }) {
        debugLog('🎴 [DealManagerRenderer] animateDealBacigaHand called, dealerIndex:', dealerIndex, 'toritoInfo:', toritoInfo);

        // Check if deck is rendered
        if (!this.deckRenderer.container) {
            console.error('🎴 [DealManagerRenderer] ERROR: Deck container not rendered! Cannot animate deal.');
            console.error('🎴 [DealManagerRenderer] Make sure to call deckRenderer.render() before dealing.');
            return;
        }

        debugLog('🎴 [DealManagerRenderer] Deck has', this.deckRenderer.cardSprites?.length || 0, 'card sprites');

        const dealingOrder = this.dealManager.getDealingOrder(dealerIndex);
        debugLog('🎴 [DealManagerRenderer] Dealing order:', dealingOrder);

        // Clear all cards (no center cards in Baciga)
        this.clearAllCards();

        // Store torito info for use in animateDealToPlayer
        this.currentToritoInfo = toritoInfo;

        // Deal 3 cards to each player, one at a time
        // Round 1: each player gets 1st card
        // Round 2: each player gets 2nd card
        // Round 3: each player gets 3rd card
        for (let round = 0; round < 3; round++) {
            debugLog(`🎴 [DealManagerRenderer] Dealing round ${round + 1}...`);
            for (const playerIndex of dealingOrder) {
                await this.animateDealToPlayer(playerIndex, round);
                await this.delay(this.dealDelay);
            }
        }

        // Clear torito info after dealing
        this.currentToritoInfo = null;

        // Log final card counts
        for (let i = 0; i < 4; i++) {
            const sprites = this.getPlayerCardSprites(i);
            debugLog(`🎴 [DealManagerRenderer] Player ${i} has ${sprites.length} card sprites`);
        }
    }

    /**
     * Flip torito cards face-down for AI players when voting starts
     * Human player (index 0) keeps their cards face-up
     * Only flips the first card (index 0) which was shown during torito
     */
    async flipToritoCardsFaceDown() {
        debugLog('🎴 [DealManagerRenderer] Flipping torito cards face-down for AI players');

        const flipPromises = [];

        // Flip first card for AI players (indices 1, 2, 3)
        for (let playerIndex = 1; playerIndex < 4; playerIndex++) {
            const container = this.playerCardsContainers[playerIndex];
            if (!container || container.children.length === 0) continue;

            const firstCardSprite = container.children[0];
            if (!firstCardSprite || !firstCardSprite.isFaceUp) continue;

            debugLog(`🎴 [DealManagerRenderer] Flipping torito card for AI player ${playerIndex}`);

            // Get card back texture
            const backTexture = await Assets.load(this.getCardBackPath());
            const currentRotation = firstCardSprite.rotation;

            // Flip to face-down
            const flipPromise = CardAnimations.flipCard(firstCardSprite, backTexture, currentRotation, 300)
                .then(() => {
                    if (firstCardSprite && !firstCardSprite.destroyed) {
                        firstCardSprite.isFaceUp = false;
                        debugLog(`🎴 [DealManagerRenderer] Torito card flipped for player ${playerIndex}`);
                    }
                });

            flipPromises.push(flipPromise);
        }

        await Promise.all(flipPromises);
        debugLog('🎴 [DealManagerRenderer] All torito cards flipped');
    }

    /**
     * Animate discarding cards to center for Baciga "paso" phase
     * Cards are thrown face-down to the center pile
     *
     * @param {number} playerIndex - Index of the player discarding
     * @param {number[]} cardIndices - Indices of cards to discard (0-2)
     */
    async animateBacigaDiscard(playerIndex, cardIndices) {
        debugLog(`🎴 [DealManagerRenderer] Player ${playerIndex} discarding cards at indices:`, cardIndices);

        const playerSprites = this.getPlayerCardSprites(playerIndex);
        debugLog(`🎴 [DealManagerRenderer] Player ${playerIndex} has ${playerSprites.length} sprites before discard`);

        // Sort indices in descending order to avoid index shifting issues when removing
        const sortedIndices = [...cardIndices].sort((a, b) => b - a);
        debugLog(`🎴 [DealManagerRenderer] Sorted indices for removal:`, sortedIndices);

        // Collect the sprites first (before any modifications)
        const spritesToDiscard = sortedIndices.map(idx => playerSprites[idx]).filter(s => s);
        debugLog(`🎴 [DealManagerRenderer] Sprites to discard:`, spritesToDiscard.length);

        for (const sprite of spritesToDiscard) {
            if (!sprite) continue;

            // Get discard position (center with random offset)
            const targetPos = this.getBacigaDiscardPosition(this.centerCardsContainer.children.length);

            // Move to center container
            const playerContainer = this.playerCardsContainers[playerIndex];
            if (playerContainer && sprite && sprite.parent === playerContainer) {
                playerContainer.removeChild(sprite);
            }
            if (this.centerCardsContainer && sprite) {
                this.centerCardsContainer.addChild(sprite);
            }

            // Animate to discard pile
            await Promise.all([
                CardAnimations.slideCard(sprite, { x: targetPos.x, y: targetPos.y }, this.dealDuration),
                this.animateRotation(sprite, targetPos.rotation, this.dealDuration)
            ]);
        }

        const remainingSprites = this.getPlayerCardSprites(playerIndex);
        debugLog(`🎴 [DealManagerRenderer] Player ${playerIndex} has ${remainingSprites.length} sprites after discard`);
    }

    /**
     * Animate dealing replacement cards after all players have passed
     * Each player gets cards dealt to bring their hand back to 3
     * For player 0 (human): cards are face-up and inserted at original positions
     * For other players: cards are face-down and appended
     * 
     * @param {number} dealerIndex - The dealer's index
     * @param {Object} newCards - { playerIndex: { cards: [cardObjects], positions: [indices] } }
     */
    async animateDealReplacementCards(dealerIndex, newCards) {
        debugLog(`🎴 [DealManagerRenderer] animateDealReplacementCards, dealer:`, dealerIndex);
        debugLog(`🎴 [DealManagerRenderer] New cards to deal:`, newCards);

        const dealingOrder = this.dealManager.getDealingOrder(dealerIndex);

        // Count how many cards each player needs
        const cardsNeeded = {};
        for (const playerIdx of dealingOrder) {
            cardsNeeded[playerIdx] = newCards[playerIdx]?.cards?.length || 0;
        }
        debugLog(`🎴 [DealManagerRenderer] Cards needed per player:`, cardsNeeded);

        // Track which card index we're dealing to each player
        const cardsDealingIndex = { 0: 0, 1: 0, 2: 0, 3: 0 };

        // Complete all cards for one player before moving to the next (not round-robin)
        for (const playerIndex of dealingOrder) {
            while (cardsDealingIndex[playerIndex] < cardsNeeded[playerIndex]) {
                const cardData = newCards[playerIndex];
                const card = cardData.cards[cardsDealingIndex[playerIndex]];
                const position = cardData.positions[cardsDealingIndex[playerIndex]];
                await this.animateDealReplacementToPlayer(playerIndex, card, position);
                cardsDealingIndex[playerIndex]++;
                await this.delay(this.dealDelay);
            }
        }

        debugLog(`🎴 [DealManagerRenderer] Replacement dealing complete`);
    }

    /**
     * Animate dealing a single replacement card to a player
     * For player 0: face-up, inserted at specific position
     * For others: face-down, appended to end
     * 
     * @param {number} playerIndex - Index of the player
     * @param {Object} card - The card object being dealt
     * @param {number} targetPosition - The position in hand where this card should go
     */
    async animateDealReplacementToPlayer(playerIndex, card, targetPosition) {
        const isHumanPlayer = isLocalPlayer(playerIndex);
        debugLog(`🎴 [DealManagerRenderer] Dealing replacement ${card.value} ${card.suit} to player ${playerIndex} at position ${targetPosition}, faceUp: ${isHumanPlayer}`);

        if (!this.deckRenderer.container || this.deckRenderer.cardSprites.length === 0) {
            console.error('🎴 [DealManagerRenderer] No deck cards available for replacement');
            return;
        }

        const topCardSprite = this.deckRenderer.cardSprites[this.deckRenderer.cardSprites.length - 1];
        if (!topCardSprite) return;

        // Create card sprite - face-up for human player, face-down for others
        const cardSprite = await this.createCardSprite(card, isHumanPlayer);

        // Start at deck position
        const deckWorldPos = this.deckRenderer.container.toGlobal({ x: 0, y: 0 });
        cardSprite.x = deckWorldPos.x;
        cardSprite.y = deckWorldPos.y;
        cardSprite.rotation = this.deckRenderer.rotation;

        // Insert at the correct position in the container for ALL players
        // This ensures z-indexing and array order matches the logical order
        const container = this.playerCardsContainers[playerIndex];
        if (targetPosition >= container.children.length) {
            container.addChild(cardSprite);
        } else {
            container.addChildAt(cardSprite, targetPosition);
        }

        // Get target position for animation
        // Visual position now respects the logical targetPosition for everyone
        const targetPos = this.getBacigaPlayerCardPosition(playerIndex, targetPosition, 3);

        // Animate to position
        await Promise.all([
            CardAnimations.slideCard(cardSprite, { x: targetPos.x, y: targetPos.y }, this.dealDuration),
            this.animateRotation(cardSprite, targetPos.rotation, this.dealDuration)
        ]);

        // Remove visual card from deck
        await this.deckRenderer.removeCard(topCardSprite.deckIndex, false);
    }

    /**
     * Get card position for Baciga player hand (stacked with fan effect for player 0)
     * Uses the same positioning as getPlayerCardPosition for consistency
     * 
     * @param {number} playerIndex - 0=bottom, 1=left, 2=top, 3=right
     * @param {number} cardIndex - Which card (0, 1, 2)
     * @param {number} totalCards - Total cards in hand (usually 3)
     */
    getBacigaPlayerCardPosition(playerIndex, cardIndex, totalCards) {
        // Use the same positioning as the initial deal for consistency
        return this.getPlayerCardPosition(playerIndex, cardIndex);
    }

    /**
     * Animate moving player cards to "Las 31" position (face-up spread)
     * Cards of the same value are grouped together with a slight offset
     * Used when transitioning from voting phase to 31 game
     * 
     * @param {number} playerIndex - Index of the player
     */
    async animateToLas31Position(playerIndex) {
        debugLog(`🎴 [DealManagerRenderer] Moving player ${playerIndex} cards to Las 31 position`);

        const sprites = this.getPlayerCardSprites(playerIndex);
        const playerCards = this.dealManager.getPlayerCards(playerIndex);

        // Group cards by value to determine unique positions and stack offsets
        const valueGroups = {};
        playerCards.forEach((card, idx) => {
            const sprite = sprites[idx];

            // Updated logic: Unbound wildcards AND hidden cards must be distinct (not stacked)
            // Hidden cards (face-down) should not reveal their value by stacking with same-value cards
            const isWildcard = (card.value === 7 && (card.suit === 'oros' || card.suit === 'copas'));
            const isUnboundWildcard = isWildcard && !card.assignedValue;
            const isHidden = sprite && (!sprite.isFaceUp || sprite.isCovered);

            let key;
            if (isUnboundWildcard || isHidden) {
                // Unique key for unbound wildcards or hidden cards (e.g., 'hidden_oros_0')
                // This ensures hidden cards do NOT stack with cards of same value
                key = `hidden_${card.suit}_${idx}`;
            } else {
                // Use assigned value for stacking if present (so wildcards stack with their bound value)
                key = card.assignedValue || card.value;
            }

            if (!valueGroups[key]) {
                valueGroups[key] = [];
            }
            valueGroups[key].push(idx);
        });

        // Assign each card a unique position index and stack offset
        // Cards with same value share a position but have different stack offsets
        const uniqueValues = Object.keys(valueGroups);
        const totalUniquePositions = uniqueValues.length;

        // Map each card index to its position index and stack offset
        const cardPositions = {};
        uniqueValues.forEach((val, positionIndex) => {
            valueGroups[val].forEach((cardIdx, stackIndex) => {
                cardPositions[cardIdx] = {
                    positionIndex,
                    stackOffset: stackIndex
                };
            });
        });

        debugLog(`🎴 [DealManagerRenderer] Player ${playerIndex} card grouping:`, cardPositions);

        const animations = sprites.map(async (sprite, cardIndex) => {
            const { positionIndex, stackOffset } = cardPositions[cardIndex] || { positionIndex: cardIndex, stackOffset: 0 };
            const targetPos = this.getBaciga31Position(playerIndex, positionIndex, totalUniquePositions, stackOffset);

            await Promise.all([
                CardAnimations.slideCard(sprite, { x: targetPos.x, y: targetPos.y }, this.dealDuration),
                this.animateRotation(sprite, targetPos.rotation, this.dealDuration)
            ]);
        });

        await Promise.all(animations);
    }

    /**
     * Animate all players moving their cards to Las 31 positions
     */
    async animateAllToLas31() {
        debugLog('🎴 [DealManagerRenderer] Moving all players to Las 31 positions');

        // Move all players simultaneously
        await Promise.all([
            this.animateToLas31Position(0),
            this.animateToLas31Position(1),
            this.animateToLas31Position(2),
            this.animateToLas31Position(3)
        ]);
    }

    /**
     * Animate dealing an additional card to a player during Las 31
     * Card comes from deck and goes to the end of their spread
     * 
     * @param {number} playerIndex - Index of the player
     * @param {boolean} faceDown - Whether card should be dealt face-down (covered)
     * @returns {Promise<Object>} The card sprite that was dealt
     */
    async animateDealCardFor31(playerIndex, faceDown = false) {
        debugLog(`🎴 [DealManagerRenderer] Dealing additional card to player ${playerIndex} for Las 31 (faceDown: ${faceDown})`);

        if (!this.deckRenderer.container || this.deckRenderer.cardSprites.length === 0) {
            console.error('🎴 [DealManagerRenderer] No deck cards available');
            return null;
        }

        const topCardSprite = this.deckRenderer.cardSprites[this.deckRenderer.cardSprites.length - 1];
        if (!topCardSprite) return null;

        // Get current card count for this player (new card goes at end)
        const currentSprites = this.getPlayerCardSprites(playerIndex);
        const newCardIndex = currentSprites.length;

        // Get the card data (should have been dealt by DealManager already)
        const playerCards = this.dealManager.getPlayerCards(playerIndex);
        const card = playerCards[newCardIndex];
        if (!card) {
            console.error(`🎴 [DealManagerRenderer] No card data at index ${newCardIndex} for player ${playerIndex}`);
            return null;
        }

        // Card can be face-up or face-down based on parameter
        const cardSprite = await this.createCardSprite(card, !faceDown);

        // Start at deck position
        const deckWorldPos = this.deckRenderer.container.toGlobal({ x: 0, y: 0 });
        cardSprite.x = deckWorldPos.x;
        cardSprite.y = deckWorldPos.y;
        cardSprite.rotation = this.deckRenderer.rotation;

        // Add to player container
        this.playerCardsContainers[playerIndex].addChild(cardSprite);

        // Get target position (Las 31 spread position)
        const totalCards = newCardIndex + 1;
        const targetPos = this.getBaciga31Position(playerIndex, newCardIndex, totalCards);

        // Animate to position
        await Promise.all([
            CardAnimations.slideCard(cardSprite, { x: targetPos.x, y: targetPos.y }, this.dealDuration),
            this.animateRotation(cardSprite, targetPos.rotation, this.dealDuration)
        ]);

        // Remove visual card from deck
        await this.deckRenderer.removeCard(topCardSprite.deckIndex, false);

        // Re-position all cards of this player to maintain proper spread with grouping
        await this.repositionPlayerCardsFor31(playerIndex);

        return cardSprite;
    }

    /**
     * Reposition all cards of a player to maintain proper Las 31 spread
     * Groups cards of the same value together with a slight offset
     * Called after adding a new card
     * 
     * @param {number} playerIndex - Index of the player
     */
    async repositionPlayerCardsFor31(playerIndex) {
        const sprites = this.getPlayerCardSprites(playerIndex);
        const playerCards = this.dealManager.getPlayerCards(playerIndex);

        // Group cards by value to determine unique positions and stack offsets
        const valueGroups = {};
        playerCards.forEach((card, idx) => {
            const sprite = sprites[idx];

            // Updated logic: Unbound wildcards AND hidden cards must be distinct (not stacked)
            // Hidden cards (face-down) should not reveal their value by stacking with same-value cards
            const isWildcard = (card.value === 7 && (card.suit === 'oros' || card.suit === 'copas'));
            const isUnboundWildcard = isWildcard && !card.assignedValue;
            const isHidden = sprite && (!sprite.isFaceUp || sprite.isCovered);

            let key;
            if (isUnboundWildcard || isHidden) {
                // Each unbound wildcard or hidden card gets its own position (no grouping)
                key = `hidden_${card.suit}_${idx}`;
            } else {
                key = card.assignedValue || card.value;
            }

            if (!valueGroups[key]) {
                valueGroups[key] = [];
            }
            valueGroups[key].push(idx);
        });

        // Assign positions with grouping
        const uniqueValues = Object.keys(valueGroups);
        const totalUniquePositions = uniqueValues.length;

        const cardPositions = {};
        uniqueValues.forEach((val, positionIndex) => {
            valueGroups[val].forEach((cardIdx, stackIndex) => {
                cardPositions[cardIdx] = {
                    positionIndex,
                    stackOffset: stackIndex
                };
            });
        });

        const animations = sprites.map(async (sprite, cardIndex) => {
            const { positionIndex, stackOffset } = cardPositions[cardIndex] || { positionIndex: cardIndex, stackOffset: 0 };
            const targetPos = this.getBaciga31Position(playerIndex, positionIndex, totalUniquePositions, stackOffset);

            await Promise.all([
                CardAnimations.slideCard(sprite, { x: targetPos.x, y: targetPos.y }, 200), // Quick reposition
                this.animateRotation(sprite, targetPos.rotation, 200)
            ]);
        });

        await Promise.all(animations);
    }

    /**
     * Animate smooth rotation
     * @param {Sprite} sprite - The sprite to rotate
     * @param {number} targetRotation - Target rotation in radians
     * @param {number} duration - Animation duration
     */
    animateRotation(sprite, targetRotation, duration) {
        return new Promise((resolve) => {
            const start = performance.now();
            const startRotation = sprite.rotation;

            // Normalize angles to find shortest path
            let diff = targetRotation - startRotation;
            while (diff > Math.PI) diff -= 2 * Math.PI;
            while (diff < -Math.PI) diff += 2 * Math.PI;

            const animate = (time) => {
                if (sprite.destroyed) return;
                const t = Math.min(1, (time - start) / duration);
                const easedT = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

                sprite.rotation = startRotation + diff * easedT;

                if (t < 1) {
                    requestAnimationFrame(animate);
                } else {
                    sprite.rotation = targetRotation;
                    resolve();
                }
            };

            requestAnimationFrame(animate);
        });
    }

    /**
     * Animate all cards returning to the deck
     * @param {Object} deckPosition - Target position {x, y} for the deck
     */
    async animateCardsReturnToDeck(deckPosition) {
        const allSprites = [];
        
        // Collect all center card sprites
        this.centerCardsContainer.children.forEach(sprite => {
            if (sprite && !sprite.destroyed) {
                allSprites.push(sprite);
            }
        });
        
        // Collect all player card sprites
        for (let i = 0; i < 4; i++) {
            this.playerCardsContainers[i].children.forEach(sprite => {
                if (sprite && !sprite.destroyed) {
                    allSprites.push(sprite);
                }
            });
        }
        
        if (allSprites.length === 0) return;
        
        // Animate all cards to deck position simultaneously
        const duration = 800;
        const promises = allSprites.map(sprite => {
            return CardAnimations.slideCard(
                sprite,
                { x: deckPosition.x, y: deckPosition.y },
                duration
            );
        });
        
        await Promise.all(promises);
        
        // After animation, destroy all sprites
        allSprites.forEach(sprite => {
            if (!sprite.destroyed) {
                sprite.destroy();
            }
        });
    }

    /**
     * Clear all cards from the table (destroys sprites)
     */
    clearAllCards() {
        // Destroy and clear center cards
        this.centerCardsContainer.children.forEach(sprite => sprite.destroy());
        this.centerCardsContainer.removeChildren();

        // Clear all player cards
        this.clearPlayerCards();
    }

    /**
     * Clear only player cards (destroys sprites)
     */
    clearPlayerCards() {
        for (let i = 0; i < 4; i++) {
            this.playerCardsContainers[i].children.forEach(sprite => sprite.destroy());
            this.playerCardsContainers[i].removeChildren();
        }
    }

    /**
     * Utility delay function
     */
    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * Get all center card sprites
     */
    getCenterCardSprites() {
        return this.centerCardsContainer.children;
    }

    /**
     * Get card sprites for a specific player
     */
    getPlayerCardSprites(playerIndex) {
        return this.playerCardsContainers[playerIndex]?.children || [];
    }

    /**
     * Update card sizes
     */
    setCardSize(width, height) {
        this.cardWidth = width;
        this.cardHeight = height;

        // Update existing cards
        this.centerCardsContainer.children.forEach(sprite => {
            sprite.width = width;
            sprite.height = height;
        });

        Object.values(this.playerCardsContainers).forEach(container => {
            container.children.forEach(sprite => {
                sprite.width = width;
                sprite.height = height;
            });
        });
    }

    /**
     * Animate a player dropping a card to the center
     * @param {number} playerIndex
     * @param {Sprite} cardSprite
     * @param {number} centerIndex - The index where the card will land in the center
     */
    async animateCardDrop(playerIndex, cardSprite, centerIndex) {
        // Remove from player container and add to center container (keeping world position)
        const worldPos = cardSprite.toGlobal({ x: 0, y: 0 });
        const centerContainerWorldPos = this.centerCardsContainer.toGlobal({ x: 0, y: 0 }); // Usually 0,0 but just in case

        const playerContainer = this.playerCardsContainers[playerIndex];
        if (playerContainer && cardSprite && cardSprite.parent === playerContainer) {
            playerContainer.removeChild(cardSprite);
        }
        if (this.centerCardsContainer && cardSprite) {
            this.centerCardsContainer.addChild(cardSprite);
        }

        cardSprite.x = worldPos.x - centerContainerWorldPos.x;
        cardSprite.y = worldPos.y - centerContainerWorldPos.y;

        // If the card was face down (AI), flip it up
        if (!cardSprite.isFaceUp) {
            const texture = await Assets.load(this.getCardImagePath(cardSprite.cardData.suit, cardSprite.cardData.value));
            cardSprite.texture = texture;
            cardSprite.isFaceUp = true;
        }

        // Search for an existing card with the same value to stack on top
        const matchingCardSprite = this.centerCardsContainer.children.find(child =>
            child !== cardSprite && // Don't match self
            child.cardData &&
            child.cardData.value === cardSprite.cardData.value
        );

        let targetPos;
        let targetRotation = 0;

        if (matchingCardSprite) {
            // Stack on top of the existing matching card with random offset for "messy" look
            const randomOffsetX = (Math.random() - 0.5) * 30; // +/- 15px
            const randomOffsetY = (Math.random() - 0.5) * 30; // +/- 15px
            const randomRot = (Math.random() - 0.5) * 0.4; // +/- ~11 degrees

            targetPos = {
                x: matchingCardSprite.x + randomOffsetX,
                y: matchingCardSprite.y + randomOffsetY,
                slotIndex: matchingCardSprite.slotIndex
            };
            targetRotation = randomRot;

            // Reuse the slot index so it doesn't take up a new logical slot
            cardSprite.slotIndex = matchingCardSprite.slotIndex;
        } else {
            // No match, take next available slot
            const slotIndex = this.getNextAvailableSlot();
            targetPos = this.getCenterCardPosition(slotIndex);
            cardSprite.slotIndex = targetPos.slotIndex; // Store slot index
        }

        // Animate
        await Promise.all([
            CardAnimations.slideCard(cardSprite, targetPos, this.dealDuration),
            this.animateRotation(cardSprite, targetRotation, this.dealDuration)
        ]);
    }

    /**
     * Animate collecting cards to a target position (parallel)
     * @param {Array<Sprite>} cardSprites - Array of sprites to collect
     * @param {Object} targetPos - {x, y}
     */
    async animateCardCollection(cardSprites, targetPos) {
        const promises = cardSprites.map(sprite => {
            return Promise.all([
                CardAnimations.slideCard(sprite, targetPos, this.dealDuration),
                this.animateRotation(sprite, 0, this.dealDuration),
            ]);
        });

        await Promise.all(promises);
    }

    /**
     * Animate sequential collection: played card → match → consecutives → pile
     * @param {Sprite} playedCardSprite - The card played by the player
     * @param {Array<Object>} matchedCards - Array of {sprite, card} for matched cards
     * @param {Object} pilePos - Final pile position {x, y}
     */
    async animateSequentialCollection(playedCardSprite, matchedCards, pilePos) {
        if (matchedCards.length === 0) return;

        // Step 1: Animate played card to first matched card
        const firstMatch = matchedCards[0];
        await this.animateCardCollection([playedCardSprite], { x: firstMatch.sprite.x, y: firstMatch.sprite.y });

        // Step 2: Animate consecutive cards sequentially (each to the next)
        for (let i = 1; i < matchedCards.length; i++) {
            const currentSprite = matchedCards[i].sprite;
            const previousSprite = matchedCards[i - 1].sprite;

            await this.animateCardCollection([currentSprite], { x: previousSprite.x, y: previousSprite.y });
        }

        // Step 3: Animate all collected cards to pile
        const allSprites = [playedCardSprite, ...matchedCards.map(m => m.sprite)];
        await this.animateCardCollection(allSprites, pilePos);
    }

    /**
     * Destroy and cleanup
     */
    /**
     * Make a player's cards interactive
     * @param {number} playerIndex
     * @param {Function} callback - Function to call when card is clicked (card, sprite)
     */
    makePlayerCardsInteractive(playerIndex, callback) {
        const container = this.playerCardsContainers[playerIndex];
        if (!container) return;

        container.children.forEach(sprite => {
            // Store original position and scale if not already stored
            if (!sprite._originalY) {
                sprite._originalY = sprite.y;
                sprite._originalScale = sprite.scale.x;
            }

            // Enable interactivity
            sprite.eventMode = 'static';
            sprite.cursor = 'pointer';

            // Remove old listeners to avoid duplicates
            sprite.removeAllListeners();

            // Click handler
            sprite.on('pointerdown', () => {
                debugLog('Card clicked:', sprite.cardData);
                callback(sprite.cardData, sprite);
            });

            // Hover effect - enlarge and lift
            sprite.on('pointerover', () => {
                sprite.zIndex = 1000; // Bring to front
                sprite.scale.set(sprite._originalScale * 1.2);
                sprite.y = sprite._originalY - 20;
            });

            sprite.on('pointerout', () => {
                sprite.zIndex = 0;
                sprite.scale.set(sprite._originalScale);
                sprite.y = sprite._originalY;
            });
        });
    }

    /**
     * Disable interactivity for a player's cards
     * @param {number} playerIndex
     */
    disablePlayerCardsInteractivity(playerIndex) {
        const container = this.playerCardsContainers[playerIndex];
        if (!container) return;

        container.children.forEach(sprite => {
            sprite.eventMode = 'none';
            sprite.cursor = 'default';
            sprite.removeAllListeners();
        });
    }

    /**
     * Animate a single player's cards to Las 31 position
     * Wrapper around animateToLas31Position for clarity
     * 
     * @param {number} playerIndex - Index of the player
     */
    async animatePlayerToLas31(playerIndex) {
        return this.animateToLas31Position(playerIndex);
    }

    /**
     * Animate center discarded cards moving to the bottom of the deck
     * Cards slide from center pile to deck position with the deck's rotation
     */
    async animateCenterCardsToDeck() {
        debugLog('🎴 [DealManagerRenderer] Moving center cards to deck');

        const centerCards = [...this.centerCardsContainer.children];
        if (centerCards.length === 0) {
            debugLog('🎴 [DealManagerRenderer] No center cards to move');
            return;
        }

        debugLog(`🎴 [DealManagerRenderer] Moving ${centerCards.length} center cards to deck`);

        // Get deck position and rotation
        const deckPos = this.deckRenderer.container ? {
            x: this.deckRenderer.container.x,
            y: this.deckRenderer.container.y,
            rotation: this.deckRenderer.rotation || 0
        } : (this.app && this.app.screen ? { x: this.app.screen.width / 2, y: this.app.screen.height / 2, rotation: 0 } : { x: 0, y: 0, rotation: 0 });

        // Animate each card to the deck position
        const animations = centerCards.map(async (sprite, idx) => {
            // Stagger the animations slightly
            await new Promise(r => setTimeout(r, idx * 50));

            await Promise.all([
                CardAnimations.slideCard(sprite, { x: deckPos.x, y: deckPos.y }, this.dealDuration),
                this.animateRotation(sprite, deckPos.rotation, this.dealDuration)
            ]);

            // Remove from center container after animation
            if (this.centerCardsContainer && sprite && sprite.parent === this.centerCardsContainer) {
                this.centerCardsContainer.removeChild(sprite);
            }
            if (sprite && !sprite.destroyed) {
                sprite.destroy();
            }
        });

        await Promise.all(animations);
        debugLog('🎴 [DealManagerRenderer] Center cards moved to deck');
    }

    /**
     * Mark a card as covered (hidden from other players) during Las 31 phase
     * Adds visual indicator for human player to know which card is covered
     * @param {number} playerIndex - Player index (0-3)
     * @param {number} cardIndex - Index of card to mark as covered
     */
    markCardAsCovered(playerIndex, cardIndex) {
        debugLog(`🎴 [DealManagerRenderer] Marking card ${cardIndex} as covered for player ${playerIndex}`);
        
        const container = this.playerCardsContainers[playerIndex];
        if (!container) return;

        const sprites = [...container.children];
        const sprite = sprites[cardIndex];
        
        if (!sprite || !sprite.cardData) return;

        // For human player (index 0), add visual indicator that card is covered
        if (isLocalPlayer(playerIndex)) {
            sprite.isCovered = true;
            // Add semi-transparent overlay to indicate it's covered
            sprite.alpha = 0.7; // Slightly transparent
            sprite.tint = 0xCCCCFF; // Slight blue tint
        }
        
        debugLog(`🎴 [DealManagerRenderer] Card ${cardIndex} marked as covered for player ${playerIndex}`);
    }

    /**
     * Reveal a covered card (remove visual indicator, keep orientation) during Las 31 phase
     * Note: For AI players, this flips the card face-up. For human player, just removes indicator.
     * @param {number} playerIndex - Player index (0-3)
     * @param {number} cardIndex - Index of card to reveal
     * @returns {Promise} Resolves when animation completes
     */
    async revealCoveredCard(playerIndex, cardIndex) {
        debugLog(`🎴 [DealManagerRenderer] Revealing covered card ${cardIndex} for player ${playerIndex}`);
        
        const container = this.playerCardsContainers[playerIndex];
        if (!container) {
            console.warn(`⚠️ [DealManagerRenderer] No container found for player ${playerIndex}`);
            return;
        }

        const sprites = [...container.children];
        const sprite = sprites[cardIndex];
        
        if (!sprite || !sprite.cardData) {
            console.warn(`⚠️ [DealManagerRenderer] No sprite found at index ${cardIndex} for player ${playerIndex}`);
            return;
        }

        if (isLocalPlayer(playerIndex)) {
            // Human player: ONLY remove visual indicator (shadowing/tint)
            // Card is ALREADY face-up for human player, DO NOT flip or rotate
            // Just restore normal appearance
            sprite.isCovered = false;
            sprite.alpha = 1.0; // Restore full opacity (remove transparency)
            sprite.tint = 0xFFFFFF; // Remove blue tint (back to normal color)
            // NO texture change, NO rotation - card stays exactly as it is
            debugLog(`🎴 [DealManagerRenderer] Removed covered indicator for player ${playerIndex} (no flip)`);
            return; // Exit early to avoid any further processing
        } else {
            // AI player: flip card face-up (revealing to human player) WITHOUT changing orientation
            if (!sprite.isFaceUp && sprite && !sprite.destroyed) {
                const faceTexture = await Assets.load(this.getCardImagePath(sprite.cardData.suit, sprite.cardData.value));
                // Preserve current rotation - pass it to flipCard so it maintains orientation
                const currentRotation = sprite.rotation;
                await CardAnimations.flipCard(sprite, faceTexture, currentRotation, 300);
                if (sprite && !sprite.destroyed) {
                    sprite.isFaceUp = true;
                    debugLog(`🎴 [DealManagerRenderer] Revealed card ${cardIndex} for AI player ${playerIndex} (preserved orientation: ${currentRotation})`);
                }
            }
        }
    }

    /**
     * Hide (flip face-down) all cards for a specific AI player
     * Used when AI player finishes their turn in Las 31 phase to hide their cards
     * until the counting phase begins
     * @param {number} playerIndex - Index of the AI player (1-3, not 0)
     * @returns {Promise} Resolves when all cards are hidden
     */
    async hidePlayerCards(playerIndex) {
        if (isLocalPlayer(playerIndex)) {
            debugLog(`🎴 [DealManagerRenderer] Skipping hidePlayerCards for human player`);
            return;
        }

        debugLog(`🎴 [DealManagerRenderer] Hiding cards for AI player ${playerIndex}`);

        const container = this.playerCardsContainers[playerIndex];
        if (!container) {
            console.warn(`⚠️ [DealManagerRenderer] No container found for player ${playerIndex}`);
            return;
        }

        const sprites = [...container.children];
        const backTexture = await Assets.load(this.getCardBackPath());
        const flipPromises = [];

        for (const sprite of sprites) {
            if (!sprite || !sprite.cardData) continue;

            // Only flip cards that are currently face-up
            if (sprite.isFaceUp && sprite && !sprite.destroyed) {
                const currentRotation = sprite.rotation;
                const flipPromise = CardAnimations.flipCard(sprite, backTexture, currentRotation, 300)
                    .then(() => {
                        if (sprite && !sprite.destroyed) {
                            sprite.isFaceUp = false;
                        }
                    });
                flipPromises.push(flipPromise);
            }
        }

        await Promise.all(flipPromises);
        debugLog(`🎴 [DealManagerRenderer] All cards hidden for AI player ${playerIndex}`);
    }

    /**
     * Reveal all cards for all players (for visual verification after Las Cosas, etc.)
     * Shows all cards face-up while preserving their positions and orientations
     * @returns {Promise} Resolves when all cards are revealed
     */
    async revealAllPlayerCards() {
        debugLog('🎴 [DealManagerRenderer] Revealing all player cards');
        
        const revealPromises = [];
        
        for (let playerIndex = 0; playerIndex < 4; playerIndex++) {
            const container = this.playerCardsContainers[playerIndex];
            if (!container) continue;
            
            const sprites = [...container.children];
            
            for (let cardIndex = 0; cardIndex < sprites.length; cardIndex++) {
                const sprite = sprites[cardIndex];
                if (!sprite || !sprite.cardData) continue;
                
                if (isLocalPlayer(playerIndex)) {
                    // Human player: just remove covered indicators if any
                    if (sprite.isCovered) {
                        sprite.isCovered = false;
                        sprite.alpha = 1.0;
                        sprite.tint = 0xFFFFFF;
                    }
                } else {
                    // AI player: flip card face-up if not already
                    if (!sprite.isFaceUp && sprite && !sprite.destroyed) {
                        const faceTexture = await Assets.load(this.getCardImagePath(sprite.cardData.suit, sprite.cardData.value));
                        const currentRotation = sprite.rotation;
                        const flipPromise = CardAnimations.flipCard(sprite, faceTexture, currentRotation, 300)
                            .then(() => {
                                if (sprite && !sprite.destroyed) {
                                    sprite.isFaceUp = true;
                                }
                            });
                        revealPromises.push(flipPromise);
                    }
                }
            }
        }
        
        await Promise.all(revealPromises);
        debugLog('🎴 [DealManagerRenderer] All player cards revealed');
    }

    /**
     * Render cards from restored save state (without animations)
     * @param {Object} savedState - The saved game state
     */
    async renderRestoredCards(savedState) {
        debugLog('═══════════════════════════════════════════════════════');
        debugLog('🎴 RENDERIZANDO CARTAS RESTAURADAS');
        debugLog('═══════════════════════════════════════════════════════');

        debugLog('📦 Estado del renderer:');
        debugLog('   - centerCardsContainer existe:', !!this.centerCardsContainer);
        debugLog('   - playerCardsContainers[0] existe:', !!this.playerCardsContainers[0]);
        debugLog('   - playerCardsContainers[1] existe:', !!this.playerCardsContainers[1]);
        debugLog('   - playerCardsContainers[2] existe:', !!this.playerCardsContainers[2]);
        debugLog('   - playerCardsContainers[3] existe:', !!this.playerCardsContainers[3]);

        try {
            // Importar Card para crear instancias directamente
            const { default: Card } = await import('./Card.js');

            // Clear any existing sprites to avoid duplicates when restoring state
            this.clearAllCards();

            // 1. Renderizar cartas del centro
            const centerCards = savedState.centerCards || [];
            debugLog(`\n🃏 Renderizando ${centerCards.length} cartas del centro...`);

            for (let i = 0; i < centerCards.length; i++) {
                const cardData = centerCards[i];
                // Crear carta directamente en lugar de buscarla en el mazo
                const card = new Card(cardData.suit, cardData.value);

                debugLog(`   [${i}] Creando sprite para ${card.suit} ${card.value}...`);
                const sprite = await this.createCardSprite(card, true); // faceUp

                // Usar slotFillOrder para posicionar correctamente
                const slotIndex = i < this.slotFillOrder.length ? this.slotFillOrder[i] : i;
                const position = this.getCenterCardPosition(slotIndex);

                sprite.x = position.x;
                sprite.y = position.y;
                sprite.zIndex = i;
                sprite.slotIndex = slotIndex; // CRÍTICO: Guardar el slot para que getNextAvailableSlot() funcione

                debugLog(`   [${i}] Añadiendo al container en slot ${slotIndex}: x=${position.x.toFixed(2)}, y=${position.y.toFixed(2)}`);
                this.centerCardsContainer.addChild(sprite);
                debugLog(`   [${i}] ✅ Carta añadida. Container children count:`, this.centerCardsContainer.children.length);
            }

            // 2. Renderizar cartas de jugadores
            debugLog(`\n👥 Renderizando cartas de jugadores...`);

            // Construir mapa de cartas reveladas desde declarations (para fase playing31)
            const revealedCardsMap = {}; // { playerIdx: { cardIdx: true/false } }
            if (savedState.phase === 'playing31' && savedState.declarations) {
                savedState.declarations.forEach(decl => {
                    if (decl.cards && Array.isArray(decl.cards)) {
                        revealedCardsMap[decl.playerIndex] = {};
                        decl.cards.forEach((card, idx) => {
                            // Las cartas en declarations están en el orden en que fueron declaradas
                            // Necesitamos encontrar el índice correspondiente en playerCards
                            const playerCards = savedState.playerCards[decl.playerIndex] || [];
                            const cardIndex = playerCards.findIndex(pc =>
                                pc.suit === card.suit && pc.value === card.value
                            );
                            if (cardIndex !== -1) {
                                revealedCardsMap[decl.playerIndex][cardIndex] = card.isFaceUp !== false;
                            }
                        });
                    }
                });
            }

            for (let playerIdx = 0; playerIdx < 4; playerIdx++) {
                const cards = savedState.playerCards[playerIdx] || [];
                const container = this.playerCardsContainers[playerIdx];

                debugLog(`\n   Jugador ${playerIdx}: ${cards.length} cartas`);

                // Agrupar cartas si estamos en fase playing31
                let valueGroups = {};
                let cardPositions = {};
                let totalUniquePositions = cards.length;

                if (savedState.phase === 'playing31') {
                    // Agrupar cartas por valor para posiciones de Las 31
                    cards.forEach((cardData, idx) => {
                        const isWildcard = (cardData.value === 7 && (cardData.suit === 'oros' || cardData.suit === 'copas'));
                        const isUnboundWildcard = isWildcard && !cardData.assignedValue;

                        let key;
                        if (isUnboundWildcard) {
                            key = `unbound_${cardData.suit}_${idx}`;
                        } else {
                            key = cardData.assignedValue || cardData.value;
                        }

                        if (!valueGroups[key]) {
                            valueGroups[key] = [];
                        }
                        valueGroups[key].push(idx);
                    });

                    const uniqueValues = Object.keys(valueGroups);
                    totalUniquePositions = uniqueValues.length;

                    uniqueValues.forEach((val, positionIndex) => {
                        valueGroups[val].forEach((cardIdx, stackIndex) => {
                            cardPositions[cardIdx] = {
                                positionIndex,
                                stackOffset: stackIndex
                            };
                        });
                    });
                }

                for (let cardIdx = 0; cardIdx < cards.length; cardIdx++) {
                    const cardData = cards[cardIdx];
                    // Crear carta directamente en lugar de buscarla en el mazo
                    const card = new Card(cardData.suit, cardData.value);

                    // Restaurar assignedValue si existe
                    if (cardData.assignedValue) {
                        card.assignedValue = cardData.assignedValue;
                    }

                    // Determinar si la carta debe mostrarse boca arriba
                    let faceUp;
                    if (playerIdx === 0) {
                        // Jugador humano: siempre boca arriba (las tapadas se manejan con cardCoverState)
                        faceUp = true;
                    } else if (savedState.phase === 'playing31') {
                        // En fase playing31, usar el estado guardado isFaceUp si existe
                        // Esto preserva correctamente qué cartas están reveladas
                        if (cardData.isFaceUp !== undefined) {
                            faceUp = cardData.isFaceUp;
                        } else {
                            // Fallback: si no hay estado guardado, asumir boca arriba
                            faceUp = true;
                        }
                    } else {
                        // En otras fases: verificar declarations
                        if (revealedCardsMap[playerIdx] && revealedCardsMap[playerIdx][cardIdx] !== undefined) {
                            faceUp = revealedCardsMap[playerIdx][cardIdx];
                        } else {
                            faceUp = false;
                        }
                    }

                    debugLog(`      [${cardIdx}] Creando sprite para ${card.suit} ${card.value} (faceUp: ${faceUp})...`);
                    const sprite = await this.createCardSprite(card, faceUp);

                    // Guardar el estado isFaceUp en el sprite para futuras referencias
                    sprite.isFaceUp = faceUp;

                    // Determinar posición según la fase
                    let position;
                    if (savedState.phase === 'playing31') {
                        const { positionIndex, stackOffset } = cardPositions[cardIdx] || { positionIndex: cardIdx, stackOffset: 0 };
                        position = this.getBaciga31Position(playerIdx, positionIndex, totalUniquePositions, stackOffset);
                        debugLog(`      [${cardIdx}] Posición Las 31: posIdx=${positionIndex}, stack=${stackOffset}`);
                    } else {
                        position = this.getPlayerCardPosition(playerIdx, cardIdx);
                    }

                    sprite.x = position.x;
                    sprite.y = position.y;
                    sprite.rotation = position.rotation;
                    sprite.zIndex = cardIdx;

                    // Aplicar estado de carta tapada para jugador humano
                    if (playerIdx === 0 && savedState.cardCoverState && savedState.cardCoverState[0]) {
                        const coverState = savedState.cardCoverState[0];
                        if (coverState.coveredCardIndex === cardIdx) {
                            sprite.isCovered = true;
                            sprite.alpha = 0.5;
                            sprite.tint = 0x888888;
                            debugLog(`      [${cardIdx}] Carta marcada como tapada`);
                        }
                    }

                    debugLog(`      [${cardIdx}] Añadiendo al container: x=${position.x.toFixed(2)}, y=${position.y.toFixed(2)}, rot=${position.rotation.toFixed(2)}`);
                    container.addChild(sprite);
                    debugLog(`      [${cardIdx}] ✅ Carta añadida. Container children count:`, container.children.length);
                }
            }

            debugLog('\n═══════════════════════════════════════════════════════');
            debugLog('✅ TODAS LAS CARTAS RENDERIZADAS');
            debugLog('═══════════════════════════════════════════════════════');
        } catch (error) {
            console.error('[DealManagerRenderer] Error rendering restored cards:', error);
        }
    }

    /**
     * Helper: Create a card sprite
     * @param {Object} card - The card object with suit and value
     * @param {boolean} faceUp - Whether to show card face up
     * @returns {Sprite} The created sprite
     */
    async createCardSprite(card, faceUp) {
        const imagePath = faceUp ? this.getCardImagePath(card.suit, card.value) : this.getCardBackPath();
        const texture = await Assets.load(imagePath);
        const sprite = new Sprite(texture);
        sprite.width = this.cardWidth;
        sprite.height = this.cardHeight;
        sprite.anchor.set(0.5);

        // Store card data (required for game logic)
        sprite.cardData = card;
        sprite.isFaceUp = faceUp;

        return sprite;
    }

    destroy() {
        this.clearAllCards();

        // Destroy player name and avatar texts
        Object.values(this.playerNameTexts).forEach(item => {
            if (item && this.app && this.app.stage) {
                // Handle name text
                if (item.name) {
                    if (item.name.parent === this.app.stage) {
                        this.app.stage.removeChild(item.name);
                    }
                    if (!item.name.destroyed) {
                        item.name.destroy();
                    }
                }
                // Handle avatar text
                if (item.avatar) {
                    if (item.avatar.parent === this.app.stage) {
                        this.app.stage.removeChild(item.avatar);
                    }
                    if (!item.avatar.destroyed) {
                        item.avatar.destroy();
                    }
                }
            }
        });
        this.playerNameTexts = {};

        if (this.centerCardsContainer && this.app && this.app.stage) {
            if (this.centerCardsContainer.parent === this.app.stage) {
                this.app.stage.removeChild(this.centerCardsContainer);
            }
            if (!this.centerCardsContainer.destroyed) {
                this.centerCardsContainer.destroy();
            }
        }

        Object.values(this.playerCardsContainers).forEach(container => {
            if (container && this.app && this.app.stage) {
                if (container.parent === this.app.stage) {
                    this.app.stage.removeChild(container);
                }
                if (!container.destroyed) {
                    container.destroy();
                }
            }
        });

        this.playerCardsContainers = {};
    }
}

export default DealManagerRenderer;
